import { Temporal } from 'temporal-polyfill';

/**
 * Time periods, used to bucket commits into groups, from three-hour spans of a day up to
 * quarters. The math lives in `common` so that the web can label the buckets the same way.
 *
 * A period is a half-open range `[since, until)`: `until` is where the next period starts, and
 * is not part of this one.
 */
export namespace TimePeriod {
  export const PERIODS = ['3h', 'day', 'usweek', 'monthweek', 'month', 'quarter'] as const;
  export type Period = typeof PERIODS[number];

  const LENGTHS: Record<Exclude<Period, 'monthweek'>, Temporal.DurationLike> = {
    '3h': { hours: 3 },
    day: { days: 1 },
    usweek: { weeks: 1 },
    month: { months: 1 },
    quarter: { months: 3 },
  };

  /**
   * The start of the period a moment belongs to. Spans start at midnight, a `usweek` on Sunday,
   * and a `monthweek` on the 1st, 8th, 15th or 22nd.
   */
  export function periodStart(period: Period, time: Temporal.PlainDateTime): Temporal.PlainDateTime {
    if (period === '3h')
      return time.round({ smallestUnit: 'hour', roundingIncrement: 3, roundingMode: 'floor' });
    const day = time.toPlainDate();
    if (period === 'day')
      return day.toPlainDateTime();
    // Temporal numbers weekdays 1..7 starting on Monday, so `dayOfWeek % 7` is the number of
    // days since the Sunday that opens the week.
    if (period === 'usweek')
      return day.subtract({ days: day.dayOfWeek % 7 }).toPlainDateTime();
    if (period === 'monthweek')
      return day.with({ day: Math.min(Math.floor((day.day - 1) / 7), 3) * 7 + 1 }).toPlainDateTime();
    if (period === 'month')
      return day.with({ day: 1 }).toPlainDateTime();
    return day.with({ month: Math.floor((day.month - 1) / 3) * 3 + 1, day: 1 }).toPlainDateTime();
  }

  /** The end of the period a moment belongs to: the start of the next period. */
  export function periodEnd(period: Period, time: Temporal.PlainDateTime): Temporal.PlainDateTime {
    const start = periodStart(period, time);
    // A month is four `monthweek`s, starting on the 1st, 8th, 15th and 22nd. The last one runs
    // to the end of the month however long that leaves it: 7 days in February, 10 in January.
    if (period === 'monthweek')
      return start.day < 22 ? start.add({ days: 7 }) : periodEnd('month', start);
    return start.add(LENGTHS[period]);
  }

  /** The last day `[since, until)` covers, for showing a range and for APIs that take whole days. */
  export function lastDay(until: Temporal.PlainDateTime): Temporal.PlainDate {
    return until.subtract({ nanoseconds: 1 }).toPlainDate();
  }
}
